import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  Card,
  CardMedia,
  CardActions,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import imageCompression from "browser-image-compression";

import useApi from "../../hooks/useApi";
import { buildSpacesImageUrl } from "../../utils/spacesImageUrl";
import Dialog_YesNo from "./Dialog_YesNo";

const DescriptionImageGallery = ({
  entityType, // "surveysection" or "surveyquestion"
  entityId,
  images = [],
  images_fr = [],
  onChange,
  language = "en", // "en" or "fr"
}) => {
  const { uploadFile, deleteFile } = useApi();
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    index: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const currentImages = language === "fr" ? images_fr : images;
  const maxImages = 3;

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
      initialQuality: 0.8,
    };

    try {
      const compressed = await imageCompression(file, options);

      // Hard limit check
      if (compressed.size > 500 * 1024) {
        throw new Error(
          `Image still too large after compression: ${Math.round(compressed.size / 1024)}KB. Must be under 500KB.`,
        );
      }

      return compressed;
    } catch (err) {
      throw new Error(`Compression failed: ${err.message}`);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Extract extension from original file
      const extension = file.name.includes(".")
        ? file.name.substring(file.name.lastIndexOf("."))
        : ".jpg";

      // Compress
      const compressed = await compressImage(file);

      // Upload
      const currentIndex = currentImages.length;
      const suffix = language === "fr" ? "_fr" : "";
      const keyWithoutFolder = `${entityId}_${currentIndex + 1}${suffix}`;

      const result = await uploadFile(compressed, {
        folder: entityType,
        key: keyWithoutFolder,
        extension,
        originalName: file.name,
        mimeType: compressed.type,
        size: compressed.size,
      });

      // Use fullKey returned from server (includes extension)
      const newImages = [...currentImages, result.fullKey];

      if (language === "fr") {
        onChange({ images, images_fr: newImages });
      } else {
        onChange({ images: newImages, images_fr });
      }

      setSnackbar({
        open: true,
        message: `Image uploaded successfully (${Math.round(compressed.size / 1024)}KB)`,
        severity: "success",
      });
    } catch (err) {
      console.error("Upload error:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to upload image",
        severity: "error",
      });
    } finally {
      setUploading(false);
      event.target.value = ""; // Reset input
    }
  };

  const handleDelete = async (index) => {
    const key = currentImages[index];
    const keyWithoutFolder = key.replace(`${entityType}/`, "");

    try {
      // Delete from Spaces - parameters are (folder, key)
      await deleteFile(entityType, keyWithoutFolder);

      // Remove from array
      const newImages = currentImages.filter((_, i) => i !== index);

      if (language === "fr") {
        onChange({ images, images_fr: newImages });
      } else {
        onChange({ images: newImages, images_fr });
      }

      setDeleteDialog({ open: false, index: null });
    } catch (err) {
      console.error("Delete error:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete image",
        severity: "error",
      });
    }
  };

  const handleMove = (fromIndex, toIndex) => {
    const newImages = [...currentImages];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);

    // Also reorder the other language array if same length
    const otherImages = language === "fr" ? images : images_fr;
    let newOtherImages = otherImages;

    if (otherImages.length > 0) {
      newOtherImages = [...otherImages];
      if (
        fromIndex < newOtherImages.length &&
        toIndex < newOtherImages.length
      ) {
        const [movedOther] = newOtherImages.splice(fromIndex, 1);
        newOtherImages.splice(toIndex, 0, movedOther);
      }
    }

    if (language === "fr") {
      onChange({ images: newOtherImages, images_fr: newImages });
    } else {
      onChange({ images: newImages, images_fr: newOtherImages });
    }
  };

  const getImageUrl = (key) => {
    return buildSpacesImageUrl(key, { defaultFolder: "survey-images" });
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, fontWeight: "bold" }}
      >
        Description Images ({language === "fr" ? "French" : "English"}) -{" "}
        {currentImages.length}/3
      </Typography>

      <Stack spacing={2}>
        {/* Image Gallery */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {currentImages.map((key, index) => (
            <Card
              key={index}
              sx={{ width: 150 }}
            >
              <CardMedia
                component="img"
                height="150"
                image={getImageUrl(key)}
                alt={`Image ${index + 1}`}
                sx={{ cursor: "pointer", objectFit: "cover" }}
                onClick={() => setLightbox({ open: true, index })}
              />
              <CardActions sx={{ justifyContent: "space-between", p: 0.5 }}>
                <Box>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => handleMove(index, index - 1)}
                    title="Move Up"
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === currentImages.length - 1}
                    onClick={() => handleMove(index, index + 1)}
                    title="Move Down"
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteDialog({ open: true, index })}
                  title="Delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>

        {/* Upload Button */}
        {currentImages.length < maxImages && (
          <Box>
            <input
              accept="image/*"
              style={{ display: "none" }}
              id={`upload-description-image-${language}`}
              type="file"
              onChange={handleUpload}
              disabled={uploading}
            />
            <label htmlFor={`upload-description-image-${language}`}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddPhotoAlternateIcon />}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Add Image"}
              </Button>
            </label>
            <Typography
              variant="caption"
              sx={{ ml: 2, color: "text.secondary" }}
            >
              Max 3 images. Auto-compressed to 1200px, max 500KB
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, index: 0 })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Image {lightbox.index + 1} of {currentImages.length}
          <IconButton
            onClick={() => setLightbox({ open: false, index: 0 })}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <img
              src={getImageUrl(currentImages[lightbox.index])}
              alt={`Full size ${lightbox.index + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog_YesNo
        open={deleteDialog.open}
        title="Delete Image?"
        message="This will permanently delete the image from storage. Continue?"
        onYes={() => handleDelete(deleteDialog.index)}
        onNo={() => setDeleteDialog({ open: false, index: null })}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DescriptionImageGallery;
